/* ==========================================================================
   KittyClo — product
   Multi-dimensional variant selection: Color x Size from Printify.

   Changing an option updates the price, availability, the gallery and the
   ?variant= parameter without a reload.

   No price is ever formatted here. Liquid renders every variant's price into
   a <template>; this file only moves the right one into view.
   ========================================================================== */

const root = document.querySelector('[data-variant-picker]')?.closest('.product__panel')
  || document.querySelector('.product__panel');

if (root) {
  const dataEl = root.querySelector('[data-variants]');
  const variants = dataEl ? JSON.parse(dataEl.textContent) : [];

  const picker = root.querySelector('[data-variant-picker]');
  const idInput = root.querySelector('[data-variant-id]');
  const button = root.querySelector('[data-add-button]');
  const priceTarget = root.querySelector('[data-price-target]');
  const priceSource = root.querySelector('[data-price-source]');
  const gallery = document.querySelector('[data-gallery]');
  const viewport = gallery?.querySelector('.gallery__viewport');

  /* All three labels are rendered by Liquid, so no shop copy lives in this
     file. Reading them from the button's own text would break whenever the
     first variant happens to be sold out. */
  const strings = {
    addToCart: button?.dataset.addLabel || '',
    soldOut: button?.dataset.soldOutLabel || '',
    unavailable: button?.dataset.unavailableLabel || '',
  };

  /* Enable the hidden id input. Without JavaScript it stays disabled and the
     <noscript> select carries the variant instead. */
  if (idInput) idInput.disabled = false;

  /* -----------------------------------------------------------------------
     Reading + matching
     ----------------------------------------------------------------------- */

  function currentSelection() {
    if (!picker) return [];
    return Array.from(picker.querySelectorAll('input[type="radio"]:checked'))
      .sort((a, b) => Number(a.dataset.optionIndex) - Number(b.dataset.optionIndex))
      .map((input) => input.value);
  }

  function findVariant(selection) {
    return variants.find((variant) =>
      selection.every((value, i) => variant.options[i] === value)
    );
  }

  /* An option value is offered when some *available* variant carries it while
     every other option stays where it is. Mirrors the Liquid that rendered the
     first paint. */
  function isValueAvailable(optionIndex, value, selection) {
    return variants.some((variant) => {
      if (!variant.available) return false;
      if (variant.options[optionIndex] !== value) return false;
      return selection.every((sel, i) => i === optionIndex || variant.options[i] === sel);
    });
  }

  /* -----------------------------------------------------------------------
     Applying
     ----------------------------------------------------------------------- */

  function updateAvailability(selection) {
    if (!picker) return;

    picker.querySelectorAll('input[type="radio"]').forEach((input) => {
      const optionIndex = Number(input.dataset.optionIndex) - 1;
      const available = isValueAvailable(optionIndex, input.value, selection);
      const label = picker.querySelector(`label[for="${input.id}"]`);

      // Shown, struck through and unusable — never hidden.
      input.toggleAttribute('data-unavailable', !available);
      if (label) label.classList.toggle('variants__label--unavailable', !available);
    });

    selection.forEach((value, i) => {
      const readout = picker.querySelector(`[data-option-selected="${i + 1}"]`);
      if (readout) readout.textContent = value;
    });
  }

  function updatePrice(variant) {
    if (!priceTarget || !priceSource) return;
    const fresh = priceSource.content.querySelector(`[data-variant-price="${variant.id}"]`);
    if (fresh) priceTarget.innerHTML = fresh.innerHTML;
  }

  function updateButton(variant) {
    if (!button) return;

    if (!variant) {
      button.disabled = true;
      button.textContent = strings.unavailable;
      return;
    }

    button.disabled = !variant.available;
    button.textContent = variant.available ? strings.addToCart : strings.soldOut;
  }

  function setActiveThumb(mediaId) {
    gallery?.querySelectorAll('[data-gallery-thumb]').forEach((thumb) => {
      thumb.classList.toggle('is-active', thumb.dataset.galleryThumb === String(mediaId));
    });
  }

  /* One image is in view at a time on every breakpoint, so moving the gallery
     means scrolling the viewport itself. scrollIntoView would drag the page
     along with it and, on desktop, jump past the sticky column. */
  function showMedia(mediaId) {
    if (!viewport) return;

    const target = viewport.querySelector(`[data-media-id="${mediaId}"]`);
    if (!target) return;

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    viewport.scrollTo({ left: target.offsetLeft, behavior: reduced ? 'auto' : 'smooth' });
    setActiveThumb(mediaId);
  }

  function updateGallery(variant) {
    if (!variant?.featured_media) return;
    showMedia(variant.featured_media.id);
  }

  function updateUrl(variant) {
    if (!variant) return;
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url);
  }

  function apply() {
    const selection = currentSelection();
    updateAvailability(selection);

    const variant = findVariant(selection);

    if (idInput) idInput.value = variant ? variant.id : '';
    updateButton(variant);

    if (variant) {
      updatePrice(variant);
      updateGallery(variant);
      updateUrl(variant);
    }
  }

  /* -----------------------------------------------------------------------
     Wiring
     ----------------------------------------------------------------------- */

  picker?.addEventListener('change', (event) => {
    if (event.target.matches('input[type="radio"]')) apply();
  });

  /* The thumbs stay real anchors so they work with JavaScript off; here we take
     over so the page itself does not move. */
  gallery?.addEventListener('click', (event) => {
    const thumb = event.target.closest('[data-gallery-thumb]');
    if (!thumb) return;
    event.preventDefault();
    showMedia(thumb.dataset.galleryThumb);
  });

  /* Swiping the gallery has to move the marker too, or it points at whatever
     was picked last. */
  let scrollFrame = 0;
  viewport?.addEventListener(
    'scroll',
    () => {
      if (scrollFrame) return;
      scrollFrame = requestAnimationFrame(() => {
        scrollFrame = 0;
        const items = Array.from(viewport.querySelectorAll('[data-media-id]'));
        const current = items.find((item) => item.offsetLeft >= viewport.scrollLeft - item.offsetWidth / 2);
        if (current) setActiveThumb(current.dataset.mediaId);
      });
    },
    { passive: true }
  );

  apply();
}
